import React from "react"
import { connect } from "react-redux"
import { debounce } from "lodash-es"
import cn from "classnames"
import { withTranslation, type WithTranslation } from "react-i18next"
import { isTablet } from "@/common/util"
import { sizeUnit } from "@/common/locales"
import Calc from "@/common/util/calculator"
import type { State } from "@/signomatic/store/reducers"
import RadioButton from "@/signomatic/components/radio_button"
import SelectBox from "@/signomatic/components/select_box"
import Hint from "@/signomatic/components/hint"
import SignTypeLink from "@/signomatic/components/sign_type_link"
import Toolbox from "@/signomatic/components/toolbox"
import type { ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { dataWithPreset, dataWithSignTypeOverride, dataWithFiltersOverride } from "@/signomatic/app/sign_validator"
import { CloseSmall } from "@/skylticons"
import Scrollable from "@/common/components/scrollable"
import type { SignStateUpdate } from "@/signomatic/app/sign"
import ClientData from "@/common/client_data"

const SizeInput: React.FC<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
> = props => {
  if (isTablet()) {
    return <input type="number" pattern="[0-9]*" {...props} />
  }
  return <input type="text" {...props} />
}

const matchData = (w: number, h: number, data: SignData, shape?: SignDataOption): boolean => {
  if (data.max_size) {
    if (data.max_size.w > 0 && w > data.max_size.w) {
      return false
    }

    if (data.max_size.h > 0 && h > data.max_size.h) {
      return false
    }
  }

  if (shape?.name !== "triangle" && ((w === 60 && h === 60) || (w === 61 && h === 61))) {
    return false
  }

  return true
}

const matchFilters = (w: number, h: number, filters: SignDataOptionFilter, state: SignState): boolean => {
  if (filters.min_size && filters.min_size.length > 0) {
    for (const size of filters.min_size) {
      if (!size.filters || size.filters.shape === state.shape) {
        if (size.w > 0 && w < size.w) {
          return false
        }

        if (size.h > 0 && h < size.h) {
          return false
        }
      }
    }
  }

  if (filters.max_size && filters.max_size.length > 0) {
    for (const size of filters.max_size) {
      if (!size.filters || size.filters.shape === state.shape) {
        if (size.w > 0 && w > size.w) {
          return false
        }

        if (size.h > 0 && h > size.h) {
          return false
        }
      }
    }
  }

  if (filters.min_flanged_width) {
    if (["direction_left", "direction_right"].includes(state.shape)) {
      const aw = h / 2
      const flatWidth = w - aw

      if (flatWidth < filters.min_flanged_width) {
        return false
      }
    }
  }

  return true
}

interface SizeToolboxStateProps {
  data: SignData
  signType: string
  signState: SignState
}

type SizeToolboxProps = SizeToolboxStateProps & ToolboxStripItemProps & WithTranslation

interface SuggestedSize {
  label: string
  value: {
    w: number
    h: number
  }
}

interface SuggestedThickness {
  label: string
  value: number
}

type SuggestedSizes = SuggestedSize[]
type SuggestedThicknesses = SuggestedThickness[]

interface SizeToolboxState {
  width?: string
  height?: string
  suggestionSelected: boolean | null
  selectedSuggestion: SuggestedSize | null
  selectedThickness: SuggestedThickness | null
}

class SizeToolbox extends React.Component<SizeToolboxProps, SizeToolboxState> {
  readonly state: SizeToolboxState = {
    width: (sizeUnit === "in" ? this.props.signState.widthIn : this.props.signState.width)?.toString(),
    height: (sizeUnit === "in" ? this.props.signState.heightIn : this.props.signState.height)?.toString(),
    suggestionSelected: !!this.matchingSuggestion(),
    selectedSuggestion: this.matchingSuggestion(),
    selectedThickness: this.matchingThickness(),
  }

  debouncedUpdate = debounce(
    (state: Partial<SignState>) => {
      this.props.sign.updateState(state)
    },
    1000,
    { leading: false }
  )

  get suggestions(): string[] | null {
    if (this.fixedSize) {
      return sizeUnit === "in" ? this.props.data.fixed_sizes_in : this.props.data.fixed_sizes
    }

    const data = dataWithSignTypeOverride(
      dataWithPreset(dataWithFiltersOverride(this.props.data, this.props.signState), this.props.signState),
      this.props.signType,
      this.props.signState
    )

    return sizeUnit === "in" ? data.suggested_sizes_in : data.suggested_sizes
  }

  get thicknesses(): string[] | null {
    return dataWithPreset(this.props.data, this.props.signState).suggested_thickness
  }

  get fixedSize(): boolean {
    return (this.props.data.fixed_sizes || []).length > 0
  }

  componentDidMount() {
    this.props.sign.onStateUpdated.subscribe(this.setSizeFromSignState)
  }

  componentWillUnmount() {
    this.props.sign.onStateUpdated.unsubscribe(this.setSizeFromSignState)
  }

  setSizeFromSignState = ({ update, state }: SignStateUpdate) => {
    if (this.state.suggestionSelected) {
      return
    }

    if (!state.widthAuto && update.width) {
      const width = sizeUnit === "in" ? Calc.mmToIn(state.width).toString() : state.width.toString()
      this.setState({ width })
    }

    if (!state.heightAuto && update.height) {
      const height = sizeUnit === "in" ? Calc.mmToIn(state.height).toString() : state.height.toString()
      this.setState({ height })
    }
  }

  updateState(state: Partial<SignState>, debounced = false) {
    if (sizeUnit === "in") {
      if (state.width) {
        state.width = Calc.inToMm(state.width)
      }

      if (state.height) {
        state.height = Calc.inToMm(state.height)
      }
    }

    if (debounced) {
      this.debouncedUpdate(state)
    } else {
      this.props.sign.updateState(state)
    }
  }

  updateWidth = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ width: e.target.value })
    this.updateState({ width: parseFloat(e.target.value.replace(",", ".")) || 1 })
  }

  updateWidthState = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ width: e.target.value })
  }

  submitWidth = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      this.updateState({ width: parseFloat(this.state.width?.replace(",", ".") || "") || 1 })
    }
  }

  updateHeight = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ height: e.target.value })
    this.updateState({ height: parseFloat(e.target.value.replace(",", ".")) || 1 })
  }

  updateHeightState = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ height: e.target.value })
  }

  submitHeight = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      this.updateState({ height: parseFloat(this.state.height?.replace(",", ".") || "") || 1 })
    }
  }

  setWidthAuto = () => {
    this.updateState({ widthAuto: true })
  }

  setWidthManual = () => {
    this.updateState({ widthAuto: false })
  }

  setHeightAuto = () => {
    this.updateState({ heightAuto: true })
  }

  setHeightManual = () => {
    this.updateState({ heightAuto: false })
  }

  setSizeSuggested = (size: SuggestedSize | null) => {
    if (!size) {
      const options = this.suggestedSizes()
      size = this.matchingSuggestion(options) || options[0] || null
    }

    this.setState({ suggestionSelected: true, selectedSuggestion: size }, () => {
      if (size) {
        let { w, h } = size.value

        if (sizeUnit === "in") {
          w = Calc.inToMm(w)
          h = Calc.inToMm(h)
        }

        this.props.sign.updateState({ width: w, height: h })
      }
    })
  }

  setThickness = (thickness?: SuggestedThickness | null) => {
    if (!thickness) {
      const options = this.suggestedThickness()
      thickness = this.matchingThickness(options) || (options && options[0])
    }

    if (thickness) {
      this.setState({ selectedThickness: thickness }, () => {
        this.props.sign.updateState({ thickness: thickness.value })
        if (!this.state.suggestionSelected) {
          this.setSizeManual()
        }
      })
    }
  }

  setSizeManual = () => {
    this.setState({ suggestionSelected: null }, () => {
      let width = parseFloat(this.state.width?.replace(",", ".") || "")
      let height = parseFloat(this.state.height?.replace(",", ".") || "")

      if (sizeUnit === "in") {
        width = Calc.inToMm(width)
        height = Calc.inToMm(height)
      }
      this.props.sign.updateState({ width, height })
    })
  }

  suggestedThickness(): SuggestedThicknesses | null {
    if (!this.thicknesses) {
      return null
    }

    return this.thicknesses.map(thickness => {
      const value = parseFloat(thickness)
      const label = `${sizeUnit === "in" ? Calc.mmToIn(value, 2) : value} ${sizeUnit}`
      return { label, value }
    })
  }

  suggestedSizes(): SuggestedSizes {
    const { signState, data } = this.props

    if (!this.suggestions) {
      return []
    }

    const fixmethod = data.fixmethods.find(item => item.name === signState.fixmethod)
    const shape = data.shapes.find(item => item.name === signState.shape)
    const newSuggestions = ["25x10", "50x30", "60x25", "60x60", "70x30", "80x30", "100x40", "100x100", "150x100"]
    const requirementsForNewSuggestions =
      signState.thickness === 0.8 &&
      !signState.thicknessLegacySizes &&
      this.props.signType === "plastic_sign" &&
      !ClientData.admin
    const suggestions = requirementsForNewSuggestions ? newSuggestions : this.suggestions

    return suggestions
      .map(size => {
        const [w, h] = size.split("x").map(x => parseFloat(x))

        if (!w || !h) {
          return {
            label: "",
            value: { w: 0, h: 0 },
          }
        }
        const label = this.sizeLabel(w, h)
        const value = { w, h }
        return { label, value }
      })
      .filter(size => {
        let { w, h } = size.value

        if (sizeUnit === "in") {
          w = Calc.inToMm(w)
          h = Calc.inToMm(h)
        }

        if (!matchData(w, h, data, shape)) {
          return false
        }

        if (fixmethod && fixmethod.filters) {
          if (!matchFilters(w, h, fixmethod.filters, signState)) {
            return false
          }
        }

        if (shape && shape.filters) {
          if (!matchFilters(w, h, shape.filters, signState)) {
            return false
          }
        }

        return true
      })
  }

  sizeLabel(w: number, h: number) {
    let paperSize = ""
    if ((w === 148 && h === 210) || (w === 5.8 && h === 8.3)) {
      paperSize = "A5"
    } else if ((w === 210 && h === 297) || (w === 8.3 && h === 11.7)) {
      paperSize = "A4"
    } else if ((w === 297 && h === 420) || (w === 11.7 && h === 16.5)) {
      paperSize = "A3"
    }

    return paperSize ? `${w} x ${h} ${sizeUnit} (${paperSize})` : `${w} x ${h} ${sizeUnit}`
  }

  matchingSuggestion(suggestions?: SuggestedSizes) {
    if (!this.suggestions) {
      return null
    }

    if (!suggestions) {
      suggestions = this.suggestedSizes()
    }

    let { width, height } = this.props.signState
    const { widthIn, heightIn } = this.props.signState

    if (sizeUnit === "in" && widthIn && heightIn) {
      width = widthIn
      height = heightIn
    }

    for (const option of suggestions) {
      const { w, h } = option.value

      if (w === width && h === height) {
        return option
      }
    }

    return null
  }

  matchingThickness(thicknesses?: SuggestedThicknesses | null) {
    if (!thicknesses) {
      thicknesses = this.suggestedThickness()
    }

    if (!this.props.signState.thickness || !thicknesses) {
      return null
    }

    const value = this.props.signState.thickness

    for (const option of thicknesses) {
      const v = option.value

      if (v === value) {
        return option
      }
    }

    return null
  }

  hints() {
    const { signType, signState, data } = this.props

    if (
      (signState.width >= data.max_size.w || signState.height >= data.max_size.h) &&
      (signState.material === "aluminium_lacquered" || ["plastic_sign", "wood_sign", "forex_sign"].includes(signType))
    ) {
      const hintTranslationKey =
        signType === "plastic_sign" || signState.material === "aluminium_lacquered"
          ? "hint_color_and_alu_are_bigger"
          : "hint_alu_and_solid_plast_are_bigger"

      return (
        <Hint
          type="tip"
          translation={`toolbox.${hintTranslationKey}`}
          components={{ a: <SignTypeLink type="aluminium_sign"> alu sign</SignTypeLink> }}
        />
      )
    }

    return null
  }

  autoToolbox() {
    const { signState, signType, data, t } = this.props

    const fixmethod = data.fixmethods.find(method => method.name === signState.fixmethod)
    const shape = data.shapes.find(method => method.name === signState.shape)
    let disabledW = false
    let disabledH = false

    let sign_width = sizeUnit === "in" ? signState.widthIn : signState.width
    let sign_height = sizeUnit === "in" ? signState.heightIn : signState.height

    const contour_sticker = signType === "sticker" && signState.shape === "contour"

    if (signState.forcedWidth) {
      sign_width = Math.round(sizeUnit === "in" ? Calc.mmToIn(signState.forcedWidth) : signState.forcedWidth)
    }

    if (signState.forcedHeight) {
      sign_height = Math.round(sizeUnit === "in" ? Calc.mmToIn(signState.forcedHeight) : signState.forcedHeight)
    }

    if (fixmethod && fixmethod.filters && fixmethod.filters.fixed_size) {
      const { w, h } = fixmethod.filters.fixed_size

      if (w > 0) {
        disabledW = true
      }

      if (h > 0) {
        disabledH = true
      }
    }

    if (shape && shape.filters && shape.filters.fixed_size) {
      const { w, h } = shape.filters.fixed_size

      if (w > 0) {
        disabledW = true
      }

      if (h > 0) {
        disabledH = true
      }
    }

    if (signState.shape === "contour" || signType === "contour_cut") {
      disabledW = true
      disabledH = true
    }

    return (
      <div className="auto-size-toolbox">
        <div className="sizes auto">
          <div className="size">
            <label className="name paragraph-xs bold mb-0" htmlFor="sign_format_width">
              {t("toolbox.width")} ({sizeUnit})
            </label>
            <div className="input">
              <RadioButton
                id="sign_width_fixed"
                name="size-auto-width"
                checked={!signState.widthAuto}
                onChange={this.setWidthManual}
                disabled={disabledW}
                tabIndex={-1}
              />
              <div className="text-wrapper">
                <SizeInput
                  id="sign_format_width"
                  onBlur={this.updateWidth}
                  onChange={this.updateWidthState}
                  onFocus={this.setWidthManual}
                  onKeyPress={this.submitWidth}
                  value={contour_sticker ? sign_width : this.state.width}
                  disabled={disabledW}
                />
              </div>
            </div>
            <div className="input automatic">
              <RadioButton
                id="sign_width_automatic"
                name="size-auto-width"
                checked={signState.widthAuto}
                onChange={this.setWidthAuto}
                disabled={disabledW}
              />
              <label htmlFor="sign_width_automatic">{t("toolbox.automatic")}</label>
            </div>
          </div>
          <div className="size">
            <label className="name paragraph-xs bold mb-0" htmlFor="sign_height_fixed">
              {t("toolbox.height")} ({sizeUnit})
            </label>
            <div className="input">
              <RadioButton
                id="sign_height_fixed"
                name="size-auto-height"
                checked={!signState.heightAuto}
                onChange={this.setHeightManual}
                disabled={disabledH}
                tabIndex={-1}
              />
              <div className="text-wrapper">
                <SizeInput
                  id="sign_format_height"
                  onBlur={this.updateHeight}
                  onChange={this.updateHeightState}
                  onFocus={this.setHeightManual}
                  onKeyPress={this.submitHeight}
                  value={contour_sticker ? sign_height : this.state.height}
                  disabled={disabledH}
                />
              </div>
            </div>
            <div className="input automatic">
              <RadioButton
                id="sign_height_automatic"
                name="size-auto-height"
                checked={signState.heightAuto}
                onChange={this.setHeightAuto}
                disabled={disabledH}
              />
              <label htmlFor="sign_height_automatic">{t("toolbox.automatic")}</label>
            </div>
          </div>
        </div>
        {this.hints()}
      </div>
    )
  }

  manualToolbox() {
    const { signState, data, t } = this.props
    const options = this.suggestedSizes()
    const thicknessOptions = this.suggestedThickness()

    const fixmethod = data.fixmethods.find(method => method.name === signState.fixmethod)
    const shape = data.shapes.find(method => method.name === signState.shape)
    let disabledW = false
    let disabledH = false

    if (fixmethod && fixmethod.filters && fixmethod.filters.fixed_size) {
      const { w, h } = fixmethod.filters.fixed_size

      if (w > 0) {
        disabledW = true
      }

      if (h > 0) {
        disabledH = true
      }
    }

    if (shape && shape.filters && shape.filters.fixed_size) {
      const { w, h } = shape.filters.fixed_size

      if (w > 0) {
        disabledW = true
      }

      if (h > 0) {
        disabledH = true
      }
    }

    let selectedvalue: SuggestedSize | undefined = options[0]

    if (this.state.selectedSuggestion) {
      selectedvalue = options.find(
        o =>
          o.value.w === this.state.selectedSuggestion?.value.w && o.value.h === this.state.selectedSuggestion?.value.h
      )
    }

    let selectedthicknessvalue: SuggestedThickness | null = (thicknessOptions && thicknessOptions[0]) || null

    if (this.state.selectedThickness) {
      selectedthicknessvalue = thicknessOptions?.find(o => o.value === this.state.selectedThickness?.value) || null

      if (!selectedvalue) {
        selectedvalue = options.find(o => o.value.w === signState.width && o.value.h === signState.height)
      }
    }

    return (
      <div className="manual-size-toolbox">
        {options && (
          <div className="sizes">
            <div className={cn("size", "select", { "radio-hidden": this.fixedSize })}>
              <label className="name paragraph-xs bold mb-0" htmlFor="sign_format_size">
                {t(this.fixedSize ? "toolbox.sizes" : "toolbox.suggestion")}
              </label>
              <div className="input">
                <RadioButton
                  id="sign_size_predefined"
                  name="size-manual"
                  checked={!!this.state.suggestionSelected}
                  onChange={() => this.setSizeSuggested(this.state.selectedSuggestion || options[0] || null)}
                />
                <SelectBox
                  id="sign_format_size"
                  className="size-manual-select"
                  leftAlign
                  defaultValue={options[0]}
                  options={options}
                  isSearchable={false}
                  onChange={this.setSizeSuggested}
                  value={selectedvalue}
                  onMenuOpen={() => this.setSizeSuggested(this.state.selectedSuggestion || options[0] || null)}
                />
              </div>
            </div>
          </div>
        )}
        {!this.fixedSize && (
          <div className="sizes manual">
            <div className="size-radio">
              {options && (
                <div className="input">
                  <RadioButton
                    id="sign_size_fixed"
                    name="size-manual"
                    checked={!this.state.suggestionSelected}
                    onChange={this.setSizeManual}
                  />
                </div>
              )}
            </div>
            <div className="size">
              <label className="name left paragraph-xs bold mb-0" htmlFor="sign_format_width">
                {t("toolbox.width")} ({sizeUnit})
              </label>
              <div className="input">
                <div className="text-wrapper">
                  <SizeInput
                    id="sign_format_width"
                    onBlur={this.updateWidth}
                    onChange={this.updateWidthState}
                    onFocus={this.setSizeManual}
                    onKeyPress={this.submitWidth}
                    disabled={disabledW}
                    value={this.state.width}
                  />
                </div>
              </div>
            </div>
            <div className="divider">
              <CloseSmall />
            </div>
            <div className="size">
              <label className="name left paragraph-xs bold mb-0" htmlFor="sign_format_height">
                {t("toolbox.height")} ({sizeUnit})
              </label>
              <div className="input">
                <div className="text-wrapper">
                  <SizeInput
                    id="sign_format_height"
                    onBlur={this.updateHeight}
                    onChange={this.updateHeightState}
                    onFocus={this.setSizeManual}
                    onKeyPress={this.submitHeight}
                    disabled={disabledH}
                    value={signState.forcedHeight || this.state.height}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {thicknessOptions && (
          <div className="sizes thickness">
            <div className="size select">
              <div className="name paragraph-xs bold mb-0">{t("toolbox.thickness")}</div>
              <div className="input">
                <SelectBox
                  id="sign_format_thickness"
                  className="size-manual-select"
                  leftAlign
                  defaultValue={thicknessOptions[0]}
                  options={thicknessOptions}
                  isSearchable={false}
                  onChange={this.setThickness}
                  value={selectedthicknessvalue}
                  onMenuOpen={() => this.setThickness(this.state.selectedThickness || thicknessOptions[0])}
                />
              </div>
            </div>
          </div>
        )}
        {this.hints()}
      </div>
    )
  }

  render() {
    const autoSize = this.props.data.autosize || (this.props.signState && this.props.signState.shape === "contour")

    return window.innerWidth <= 1023 ? (
      <Toolbox overflow className="toolbox-size">
        <Scrollable scrollToActive scrollActiveToolboxDelay>
          {autoSize ? this.autoToolbox() : this.manualToolbox()}
        </Scrollable>
      </Toolbox>
    ) : (
      <Toolbox overflow className="toolbox-size">
        {autoSize ? this.autoToolbox() : this.manualToolbox()}
      </Toolbox>
    )
  }
}

export default connect(({ signType, signState, currentSignData }: State) => ({
  signType,
  signState,
  data: currentSignData,
}))(withTranslation()(SizeToolbox))
